import React, { useState, useEffect } from 'react';
import { useI18n } from './i18n';
import { authFetch, isAuthenticated } from './auth';
import AttachmentList from './AttachmentList';
import ProductSummary from './ProductSummary';
import PdfButton from './PdfButton';
import { PRODUCT_CATEGORIES } from './productScoring';

const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:8100/api'
  : '/api';

const FIELDS = ['name', 'producer', 'origin', 'variety', 'harvest_year'];
const toForm = (product, category) => ({
  category: (product && product.category) || category || 'coffee',
  ...Object.fromEntries(FIELDS.map((f) => [f, product && product[f] != null ? String(product[f]) : ''])),
});

// Create or edit a product of the catalog. Props: product (to edit), category (default of a new one), onDone(product), onCancel().
function ProductForm({ product, category, onDone, onCancel }) {
  const { t } = useI18n();
  const editing = !!product;
  const [values, setValues] = useState(() => toForm(product, category));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const set = (field) => (e) => setValues((v) => ({ ...v, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!values.name.trim()) { setMessage({ type: 'error', text: t('products.form.name_required') }); return; }
    setSaving(true);
    setMessage(null);
    const year = parseInt(values.harvest_year, 10);
    const body = {
      category: values.category, name: values.name.trim(), producer: values.producer.trim() || null,
      origin: values.origin.trim() || null, variety: values.variety.trim() || null, harvest_year: Number.isNaN(year) ? null : year,
    };
    try {
      const res = await authFetch(`${API_URL}/products${editing ? `/${product.id}` : ''}`, { method: editing ? 'PUT' : 'POST', body: JSON.stringify(body) });
      const data = res ? await res.json().catch(() => ({})) : {};
      if (!res || !res.ok) { setMessage({ type: 'error', text: data.detail || t('products.form.error_save') }); return; }
      onDone(data);
    } catch {
      setMessage({ type: 'error', text: t('products.form.error_save') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="sheet-form" onSubmit={submit}>
      <h2>{t(editing ? 'products.form.title_edit' : 'products.form.title_new')}</h2>
      <div className="form-field">
        <label>{t('products.f.category')}</label>
        <select value={values.category} onChange={set('category')} disabled={editing}>
          {PRODUCT_CATEGORIES.map((c) => <option key={c} value={c}>{t(`products.category.${c}`)}</option>)}
        </select>
      </div>
      <div className="form-field">
        <label>{t(`products.f.name.${values.category}`)} *</label>
        <input value={values.name} onChange={set('name')} maxLength={200} required />
      </div>
      <div className="form-grid">
        <div className="form-field"><label>{t('products.f.producer')}</label><input value={values.producer} onChange={set('producer')} maxLength={200} /></div>
        <div className="form-field"><label>{t('products.f.origin')}</label><input value={values.origin} onChange={set('origin')} maxLength={200} /></div>
        {values.category !== 'vinegar' && (
          <div className="form-field"><label>{t(`products.f.variety.${values.category}`)}</label><input value={values.variety} onChange={set('variety')} maxLength={200} /></div>
        )}
        <div className="form-field">
          <label>{t(`products.f.harvest_year.${values.category}`)}</label>
          <input type="number" min={1900} max={2200} value={values.harvest_year} onChange={set('harvest_year')} />
        </div>
      </div>
      {message && <div className={`message ${message.type}`}>{message.text}</div>}
      <div className="form-buttons">
        <button type="submit" className="submit-btn" disabled={saving}>{t(editing ? 'products.form.save_edit' : 'products.form.save')}</button>
        <button type="button" className="reset-btn" onClick={onCancel}>{t('ui.cancel')}</button>
      </div>
    </form>
  );
}

// Sheets of any type about the product (the API answers a list, or an object holding lists)
const flattenSheets = (data) => (Array.isArray(data) ? data : Object.values(data || {}).filter(Array.isArray).flat());

function ProductDetail({ productId, meId, onBack, onEdit, onDeleted }) {
  const { t, formatNumber, formatDate } = useI18n();
  const [product, setProduct] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [error, setError] = useState(false);
  const [note, setNote] = useState(null);

  useEffect(() => {
    let abort = false;
    authFetch(`${API_URL}/products/${productId}`)
      .then((r) => (r && r.ok ? r.json() : Promise.reject(r && r.status)))
      .then((p) => { if (!abort) setProduct(p); })
      .catch(() => { if (!abort) setError(true); });
    authFetch(`${API_URL}/products/${productId}/sheets`)
      .then((r) => (r && r.ok ? r.json() : []))
      .then((d) => { if (!abort) setSheets(flattenSheets(d)); })
      .catch(() => {});
    return () => { abort = true; };
  }, [productId]);

  const back = <button className="back-btn" onClick={onBack}>{t('products.back')}</button>;
  if (error) return <div className="catalog">{back}<p>{t('products.err_load')}</p></div>;
  if (!product) return <div className="catalog">{back}<p>{t('ui.loading')}</p></div>;

  const mine = !!meId && product.owner_id === meId;
  const remove = async () => {
    if (!window.confirm(t('products.confirm_delete'))) return;
    try {
      const res = await authFetch(`${API_URL}/products/${product.id}`, { method: 'DELETE' });
      const data = res ? await res.json().catch(() => ({})) : {};
      if (!res || !res.ok) throw new Error(data.detail || '');     // 409 product_in_use: localized by the API
      onDeleted();
    } catch (e) {
      setNote({ type: 'error', text: e.message || t('products.delete_error') });
    }
  };

  return (
    <div className="catalog">
      {back}
      <div className="wine-card">
        <h2>{product.name} {product.harvest_year || ''}</h2>
        <p><span className="chip selected">{t(`products.category.${product.category}`)}</span></p>
        {product.producer && <p><strong>{t('products.f.producer')}:</strong> {product.producer}</p>}
        <div className="wine-meta">
          {product.origin && <span><strong>{t('products.f.origin')}:</strong> {product.origin}</span>}
          {product.variety && <span><strong>{t(`products.f.variety.${product.category}`)}:</strong> {product.variety}</span>}
          {product.harvest_year && <span><strong>{t(`products.f.harvest_year.${product.category}`)}:</strong> {product.harvest_year}</span>}
        </div>
      </div>
      {note && <div className={`message ${note.type}`}>{note.text}</div>}
      {mine ? (
        <div className="form-buttons">
          <button type="button" className="submit-btn" onClick={() => onEdit(product)}>{t('products.edit')}</button>
          <button type="button" className="reset-btn" onClick={remove}>{t('products.delete')}</button>
        </div>
      ) : <p className="empty">{t(product.owner_id ? 'products.readonly_other' : 'products.readonly_anonymous')}</p>}

      <h3>{t('products.labels')}</h3>
      <AttachmentList ownerType="products" ownerId={product.id} canEdit={isAuthenticated()} />

      <ProductSummary productId={product.id} category={product.category} />
      <p>
        <PdfButton path={`products/${product.id}/report/pdf`} className="submit-btn" label={t('reports.report_pdf')}
          hint={t('reports.report_hint')} fallbackName={`report-${product.category}.pdf`} />
      </p>

      <h3>{t('products.sheets')} ({sheets.length})</h3>
      {sheets.length === 0 && <p className="empty">{t('products.no_sheets')}</p>}
      {sheets.length > 0 && (
        <div className="cards">
          {sheets.map((s) => (
            <div key={s.id} className="card">
              <div className="card-body">
                <p>{s.taster || '—'}</p>
                {s.total_score != null && <div className="card-score-final">{formatNumber(s.total_score, 2)}</div>}
              </div>
              <div className="card-footer">{formatDate(s.tasting_date) || '—'}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Catalogue of products. Props: category — when given ('' = all), the category is chosen by the parent (the Catalogue
// navigation shows the category chips) and the chips of this component are hidden.
function ProductCatalog({ category: controlledCategory }) {
  const { t } = useI18n();
  const [query, setQuery] = useState('');
  const [ownCategory, setCategory] = useState('');
  const controlled = controlledCategory !== undefined;
  const category = controlled ? controlledCategory : ownCategory;
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState({ name: 'list' });       // list | detail | new | edit
  const [meId, setMeId] = useState(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!isAuthenticated()) return;
    authFetch(`${API_URL}/me`).then((r) => (r && r.ok ? r.json() : null)).then((u) => { if (u) setMeId(u.id); }).catch(() => {});
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (category) params.set('category', category);
        if (query) params.set('q', query);
        const res = await fetch(`${API_URL}/products${params.toString() ? `?${params}` : ''}`, { signal: controller.signal });
        if (res.ok) setProducts(await res.json());
      } catch { /* aborted */ } finally { setLoading(false); }
    }, query ? 250 : 0);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [query, category, reload]);

  const toList = () => { setMode({ name: 'list' }); setReload((n) => n + 1); };
  const done = (p) => { setReload((n) => n + 1); setMode({ name: 'detail', id: p.id }); };

  if (mode.name === 'new') return <div className="catalog"><ProductForm category={category} onCancel={toList} onDone={done} /></div>;
  if (mode.name === 'edit') {
    return <div className="catalog"><ProductForm product={mode.product} onCancel={() => setMode({ name: 'detail', id: mode.product.id })} onDone={done} /></div>;
  }
  if (mode.name === 'detail') {
    return <ProductDetail key={`${mode.id}-${reload}`} productId={mode.id} meId={meId} onBack={toList}
      onEdit={(p) => setMode({ name: 'edit', product: p })} onDeleted={toList} />;
  }

  return (
    <div className="catalog">
      <h2>{t('products.title')}</h2>
      {!controlled && (
        <div className="chip-group" style={{ marginBottom: 10 }}>
          {[''].concat(PRODUCT_CATEGORIES).map((c) => (
            <button type="button" key={c || 'all'} className={`chip ${category === c ? 'selected' : ''}`} onClick={() => setCategory(c)}>
              {c ? t(`products.category.${c}`) : t('products.category.all')}
            </button>
          ))}
        </div>
      )}
      <div className="catalog-search">
        <input type="search" placeholder={t('products.search')} value={query} onChange={(e) => setQuery(e.target.value)} />
      </div>
      <p><button type="button" className="submit-btn" onClick={() => setMode({ name: 'new' })}>{t('products.add')}</button></p>
      {loading && <p className="empty">{t('ui.loading')}</p>}
      {!loading && products.length === 0 && <p className="empty">{t('products.none')}</p>}
      {products.length > 0 && (
        <table className="catalog-table">
          <thead>
            <tr>
              <th>{t('products.th_name')}</th>
              <th>{t('products.f.category')}</th>
              <th>{t('products.f.producer')}</th>
              <th>{t('products.th_year')}</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} onClick={() => setMode({ name: 'detail', id: p.id })}>
                <td><strong>{p.name}</strong></td>
                <td>{t(`products.category.${p.category}`)}</td>
                <td>{p.producer || '—'}</td>
                <td>{p.harvest_year || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default ProductCatalog;
